<template>
  <div class="flex items-center gap-2 cursor-pointer">
    <!-- Radio input and custom visual -->
    <div
      class="relative inline-block size-5 focus-within:outline-surface-dark outline-1 outline cursor-pointer rounded-full"
      :class="[
        modelValue === value || disabled ? 'outline-transparent' : 'outline-gray-500',
        disabled ? 'bg-surface-dark' : 'group-hover:outline-brand ',
      ]"
      :data-test-id="generateDataTestId(dataTestId, 'radiobox')"
      @click="selectValue"
    >
      <input
        :id="radioId"
        type="radio"
        class="sr-only"
        :checked="modelValue === value"
        :disabled="disabled"
        :name="name"
        :value="value"
        :aria-label="ariaLabel || label || name"
        @change="updateValue($event)"
      >
      <div
        class="absolute top-0 left-0 w-5 h-5 rounded-full transition-all duration-200 ease-in-out group-hover:border-brand"
        :class="{
          'bg-brand': modelValue === value,
          'bg-surface-light': modelValue !== value,
          'opacity-60 cursor-not-allowed bg-surface-dark': disabled,
        }"
      >
        <!-- Inner dot when checked -->
        <div
          v-if="modelValue === value"
          class="absolute w-2 h-2 bg-surface-light rounded-full left-1.5 top-1.5"
        />
      </div>
    </div>

    <!-- Label if provided -->
    <label
      v-if="label"
      class="text-sm cursor-pointer"
      :class="{
        'cursor-not-allowed text-secondary': disabled,
        'text-primary': !disabled,
      }"
      :for="radioId"
    >
      {{ label }}
    </label>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

interface Props {
  modelValue?: string | number | boolean
  value: string | number | boolean
  label?: string
  name?: string
  disabled?: boolean
  dataTestId?: string
  ariaLabel?: string
  // Custom ID for the radio input, so a parent can `<label for>` it directly
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: '',
  name: '',
  disabled: false,
  dataTestId: '',
  ariaLabel: '',
  id: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | boolean]
  'change': [value: string | number | boolean]
}>()

// Use provided ID or generate a unique one for the radio button
const radioId = ref(props.id || `am3-radio-${uuidv4()}`)

// Update model value
// Note: the native "change" event's target.value is always a string (DOM attributes
// are untyped), so re-emit the original typed prop instead of the DOM value - otherwise
// non-string values (numbers, booleans) come back mistyped and fail modelValue === value
// checks upstream, e.g. when the radio is activated via a native label click rather than
// the click handler below.
const updateValue = () => {
  emit('update:modelValue', props.value)
  emit('change', props.value)
}

// Select the value when clicking on the radio
const selectValue = () => {
  if (!props.disabled && props.modelValue !== props.value) {
    emit('update:modelValue', props.value)
    emit('change', props.value)
  }
}
</script>
